import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { squidloansUserAPI } from '../../services/api';
import { LEAD_STAGE, automationNodeLeadStageToRouteMap } from '../../Utils/constants';
import FlowFooter from '../../components/FlowFooter';
import useLeadStage from '../../hooks/useLeadStage';
import PageLoader from '../../components/Loader';

const curr_page_lead_stage = [
  LEAD_STAGE.LOAN_REQUESTED,
  LEAD_STAGE.SEND_AADHAAR_OTP,
];

function Aadhar() {
  const navigate = useNavigate();
  const { leadStage, isLoadingStage, isReloan } = useLeadStage();
  const [aadharNumber, setAadharNumber] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isReloan) {
      navigate("/apply/e-sign");
      return;
    }
  }, [isReloan, navigate]);

  const validateAdhar = (adhar) => {
    const adharRegex = /^\d{12}$/;
    return adharRegex.test(adhar);
  };

  const handleAadharChange = (e) => {
    const value = e.target.value.replace(/\s/g, ""); // Remove spaces
    if (/^\d{0,12}$/.test(value)) {
      // Format the number as 4-4-4
      let formattedValue = value;
      if (value.length > 4) {
        formattedValue = value.slice(0, 4) + " " + value.slice(4);
      }
      if (value.length > 8) {
        formattedValue =
          formattedValue.slice(0, 9) + " " + formattedValue.slice(9);
      }
      setAadharNumber(formattedValue);
      // console.log("Aadhar Number:", formattedValue);
      setErrors((prev) => ({ ...prev, adhar: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    const adharNumberClean = aadharNumber.replace(/\s/g, "");

    if (!validateAdhar(adharNumberClean)) {
      newErrors.adhar = "Please enter a valid 12-digit Aadhar number";
    }

    if (Object.keys(newErrors).length === 0) {
      try {
        setIsLoading(true);
        const data = {
          aadhaarNo: adharNumberClean,
        };

        // console.log("Initiating KYC with data:", data);
        const response = await squidloansUserAPI.initiateKYC(data);
        // console.log("KYC Initiation Response:", response);

        if (response) {
          // Save data to sessionStorage
          const stateData = {
            aadhaarNo: adharNumberClean,
            accessKey: response.accessKey,
          };
          sessionStorage.setItem("adharData", JSON.stringify(stateData));

          navigate("/apply/otp-aadhar", {
            state: stateData,
          });
        }
      } catch (error) {
        // console.error("KYC Initiation Error:", error);
        setErrors((prev) => ({
          ...prev,
          submit:
            error.response?.data?.message ||
            "Failed to process. Please try again.",
        }));
      } finally {
        setIsLoading(false);
      }
    } else {
      setErrors(newErrors);
    }
  };

  // Check routing after journey is loaded
  useEffect(() => {
    if (isLoadingStage) {
      return;
    }

    if (leadStage && !curr_page_lead_stage.includes(leadStage)) {
      navigate(automationNodeLeadStageToRouteMap[leadStage]);
    }
  }, [leadStage, navigate, isLoadingStage]);

  if (!leadStage) {
    return <PageLoader />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#F1F5F9] text-[#1E293B] relative flex flex-col">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="hidden sm:block absolute -top-20 -right-10 w-80 h-80 bg-[#FF6B35]/10 blur-[120px]"></div>
        <div className="hidden sm:block absolute top-32 -left-10 w-72 h-72 bg-[#1E3A8A]/10 blur-[110px]"></div>
        <div className="hidden sm:block absolute bottom-0 right-1/3 w-96 h-96 bg-[#FF6B35]/5 blur-[130px]"></div>
      </div>

      <div className="relative flex-1 flex flex-col pt-20 sm:pt-24 md:pt-28 pb-6 sm:pb-10 px-3 sm:px-4 md:px-6 lg:px-10 mt-0 overflow-y-auto">
        <div className="max-w-md w-full mx-auto space-y-4 sm:space-y-6 py-2 sm:py-4">
          <div className="text-center space-y-2 sm:space-y-3 mb-2 sm:mb-4 px-2">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold leading-tight text-[#1E3A8A]">
              Enter your <span className="text-[#FF6B35]">Aadhar</span> number
            </h1>
            <p className="text-sm sm:text-base text-[#64748B]">
              12-digit Aadhaar for instant verification
            </p>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-2xl shadow-[#FF6B35]/10">
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
              <div>
                <label className="block text-xs sm:text-sm font-semibold tracking-wide text-[#1E293B] mb-1">
                  Aadhar Number <span className="text-[#FF6B35]">*</span>
                </label>
                <input
                  type="text"
                  placeholder="XXXX XXXX XXXX"
                  value={aadharNumber}
                  onChange={handleAadharChange}
                  maxLength={14}
                  className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl bg-white border text-sm sm:text-base ${
                    errors.adhar
                      ? 'border-red-500'
                      : 'border-[#E2E8F0] focus:border-[#FF6B35]'
                  } text-[#1E293B] placeholder:text-[#94A3B8] text-center tracking-[0.25em] focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/40`}
                />
                {errors.adhar && (
                  <p className="text-xs text-red-500 mt-1 sm:mt-2">{errors.adhar}</p>
                )}
              </div>

              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 text-xs sm:text-sm text-red-700">
                  {errors.submit}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || aadharNumber.replace(/\s/g, '').length !== 12}
                className="w-full py-3 sm:py-3.5 rounded-xl sm:rounded-2xl bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white text-sm sm:text-base font-semibold hover:opacity-90 transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin">⏳</span>
                    Processing...
                  </span>
                ) : (
                  'Submit'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
      <FlowFooter />
    </div>
  );
}

export default Aadhar;

